import React, { useMemo, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import parse from "autosuggest-highlight/parse";
import match from "autosuggest-highlight/match";
import CircularProgress from "@mui/material/CircularProgress";
import SearchBar from "components/SearchBar";
import { useLazyQuery } from "@apollo/client";
import { GLOBAL_SEARCH } from "graphql/service";
import {
  SearchResultsContainer,
  Container,
  StyledList,
  StyledItem
} from "./styles";
import { debouncedErrorMessage } from "utils/messageUtil";

const SearchResults = ({ mobile, open }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const keyword = location.state?.keyword || "";

  const [globalSearch, { data, loading, error }] = useLazyQuery(
    GLOBAL_SEARCH,
    { fetchPolicy: "network-only" }
  );

  useEffect(() => {
    if (!keyword) return;

    globalSearch({
      variables: {
        input: { query: keyword }
      }
    });
  }, [keyword, globalSearch]);

  useEffect(() => {
    if (!error || error.networkError) return;

    let content = error.message;
    if (error.message.includes("Validation")) {
      content = JSON.stringify(
        error.graphQLErrors[0]?.extensions?.exception
      );
    }
    debouncedErrorMessage(content);
  }, [error]);

  const options = useMemo(() => {
    const groups = data?.globalSearch;
    if (!Array.isArray(groups)) return [];

    const lowerCase = keyword.toLowerCase();

    return groups.flatMap((group) =>
      group.items.map((item) => {
        const matches = match(item.matchedValue ?? "", lowerCase, {
          insideWords: true,
          findAllOccurrences: true,
        });

        return {
          ...item,
          highlightedValue: parse(item.matchedValue ?? "", matches),
        };
      })
    );
  }, [data, keyword]);

  return (
    <SearchResultsContainer mobile={mobile} open={open}>
      <SearchBar />
      <Container>
        <div className="resultTitle">
          Search results for '{keyword}'
        </div>
        <div className="totalResults">
          {options.length} RESULTS
        </div>
        {loading ? (
          <CircularProgress
            style={{ position: "absolute", top: "81px", right: "440px" }}
            color="success"
            thickness={10}
            size={20}
          />
        ) : (
          <StyledList>
            {options.map((item) => (
              <StyledItem
                key={item.entityId}
                onClick={()=>{
                  switch (item.entityType) {
                    case 'BOOKING':
                    case 'IDENTITY_SCAN':
                    case 'PAYMENT_CARD':
                    case 'TRANSACTION':
                    case 'TENANT_PARKING':
                      if(item.relatedEntities.length > 0) {
                        const group = item.relatedEntities.find((entity) => entity.entityType === 'GROUP')
                        group.id && navigate(`/booking-details/${group.id}`)
                      }
                      break
                    case 'GROUP':
                      navigate(`/booking-details/${item.entityId}`)
                      break
                    case 'USER':
                      navigate(`/tenant-details/${item.entityId}`)
                      break
                    case 'ROOM':
                      navigate(`/room-details/${item.entityId}`)
                      break
                    default:
                      break
                  }
                }}
              >
                <span className="type">{item.entityType}: </span>

                {item.highlightedValue.map((str, index) => (
                  <span
                    key={index}
                    className={str.highlight ? "highlight" : undefined}
                  >
                    {str.text}
                  </span>
                ))}
              </StyledItem>
            ))}
          </StyledList>
        )}
        <div className="resultBottom"></div>
      </Container>
    </SearchResultsContainer>
  );
};

export default SearchResults;